import { useTheme, useMediaQuery } from '@mui/material';
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Button,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import DriverManagement from "./driverComponents/DriverManagement";

import { useDispatch, useSelector } from "react-redux";
import { Box, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { makeStyles } from "@mui/styles";
import DriverSave from "./driverSave1";
import {
  deleteDriverInfoById,
  getAllDriverInfo,
} from "../../redux/actions/driverInfoActions";
import { getGeneralPartyInfoById } from "../../redux/actions/generalPartyInfoActions";
import Loader from "../loader/loader";
import DriverViolationsDialog from "./DriverViolationsDialog";

const useStyles = makeStyles({
  root: {
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "none",
    },
    "& .MuiDataGrid-window": {
      borderTop: "none",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: '"Poppins", sans-serif',
    overflowX: "auto",
    display: "block",
    "@media (min-width: 600px)": {
      display: "table",
    },
  },
  thead: {
    backgroundColor: "#f5f5f5",
    textAlign: "left",
    fontWeight: 600,
    fontSize: "16px",
    "@media (max-width: 600px)": {
      fontSize: "14px",
    },
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "500",
    borderBottom: "2px solid #ddd",
    "@media (max-width: 600px)": {
      padding: "8px",
      fontSize: "14px",
    },
  },
  tbody: {
    fontSize: "14px",
    color: "#333",
    "@media (max-width: 600px)": {
      fontSize: "12px",
    },
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #ddd",
    fontWeight: "500",
    "@media (max-width: 600px)": {
      padding: "8px",
      fontSize: "12px",
    },
  },
  actionCell: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    "@media (max-width: 600px)": {
      gap: "6px",
    },
  },
});

const DriverInformationList = forwardRef(function DriverInformationList(props, ref) {
  const { handleNext, handleBack, id } = props;
  const childRef = useRef(null);
  const getGpId = useSelector((state) => state.generalPartyInfo.gpId);
  const dispatch = useDispatch();
  const quoteId = getGpId;

  const classes = useStyles();
  const [showButton, setShowButton] = useState(false);
  const [showHideDriverForm, setShowHideDriverForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const allDriverInfo = useSelector((state) => state.driverInfo.allDriverInfo);
  const generalPartyInfo = useSelector((state) => state.generalPartyInfo.allGeneralPartyInfo);

  const [rows, setRows] = useState([]);
  const [driverData, setDriverData] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  // Violations
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [driversData, setDriversData] = useState([]);

  const handleOpenDialogDriverViolation = (driverId) => {
    setSelectedDriverId(driverId);
    setDialogOpen(true);
  };
  const handleCloseDialogViolation = () => {
    setDialogOpen(false);
    setSelectedDriverId(null);
  };
  const handleSaveViolations = (driverId, violations) => {
    const updatedDrivers = driversData.map((driver) =>
      driver.id === driverId ? { ...driver, violations } : driver
    );
    setDriversData(updatedDrivers);
  };
  const getDriverViolations = (driverId) => {
    const driver = driversData.find((d) => d.id === driverId);
    return driver?.violations || [];
  };

  // Delegation for modal
  useImperativeHandle(ref, () => ({
    shouldConfirm: () => childRef.current?.shouldConfirm?.() || false,
    saveWithoutNav: () =>
      childRef.current?.saveWithoutNav?.() || Promise.resolve({ ok: true }),
    resetForm: () => childRef.current?.resetForm?.(),
  }));

  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const [formState, setFormState] = useState({
    dob: "",
    driverTrainingDate: "",
    driverLicenseDate: "",
    fieldErrors: {},
  });

  useEffect(() => {
    if (id) {
      dispatch(getGeneralPartyInfoById(id));
    } else if (quoteId) {
      dispatch(getGeneralPartyInfoById(quoteId));
    }
  }, [id, quoteId, dispatch]);

  useEffect(() => {
    if (quoteId) {
      dispatch(getAllDriverInfo(quoteId));
    } else if (id) {
      dispatch(getAllDriverInfo(id));
    }
  }, [id, quoteId, dispatch]);

  useEffect(() => {
    allDriverInfo.filter((row) => (row.id = row.did)); // (kept as-is)
    setRows(allDriverInfo || []);
  }, [allDriverInfo]);

  const handleOpenDialog = (rowId) => {
    setDriverToDelete(rowId);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDriverToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (driverToDelete === null || driverToDelete === undefined) return;
    handleCloseDialog();
    setIsLoading(true);
    const response = await dispatch(deleteDriverInfoById(driverToDelete));
    if (response) {
      setRows((r) => r.filter((row) => row.id !== driverToDelete));
    }
    setIsLoading(false);
  };

  const handleEditClick = (rowId) => async () => {
    setShowHideDriverForm(true);
    setShowButton(true);
    const editedRow = rows.find((row) => row.id === rowId);
    setDriverData(editedRow || {});
    if (editedRow) {
      setFormState((prevState) => ({
        ...prevState,
        dob: editedRow?.dob || "",
        driverTrainingDate: editedRow?.driverTrainingDate || "",
        driverLicenseDate: editedRow?.driverLicenseDate || "",
        driverNumber: editedRow?.driverNumber || "",
      }));
    }
  };

  function handleClick() {
    setShowHideDriverForm(true);

    if (rows.length === 0 && generalPartyInfo) {
      const preFilledData = {
        firstName: generalPartyInfo.firstName || "",
        lastName: generalPartyInfo.lastName || "",
        dob: generalPartyInfo.dob
          ? dayjs(generalPartyInfo.dob).format("MM-DD-YYYY")
          : "",
        gender: generalPartyInfo.gender || "",
      };
      setDriverData(preFilledData);
    } else {
      setDriverData({});
    }
  }

  return (
    <>
      {showHideDriverForm ? (
        <>
          <Box sx={{ marginLeft: "0px" }}>
            <DriverSave
              ref={childRef}
              setShowHideDriverForm={setShowHideDriverForm}
              driverData={driverData}
              setshowbutton={showButton}
              id={id ?? quoteId}
              rows={rows}
              showHideDriverForm={showHideDriverForm}
            />
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              padding: "0px 0px 34px",
              boxSizing: "border-box",
              lineHeight: "normal",
              letterSpacing: "normal",
            }}
          >
            <DriverManagement handleBack={handleBack} handleClick={handleClick} />
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "center",
                padding: "0px 20px",
                boxSizing: "border-box",
                maxWidth: "100%",
                '@media (min-width: 768px) and (max-width: 1024px)': {
                  minHeight: '100vh',
                  height: 'auto',
                  overflowY: 'auto',
                },
                '@media (min-width: 1024px) and (min-height: 1366px)': {
                  height: 'auto',
                  minHeight: '65vh',
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "853px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "flex-start",
                  gap: "24px",
                }}
              >
                <Paper
                  sx={{
                    alignSelf: "stretch",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    border: "1px solid #e3e3e3",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    padding: isMobile ? "20px" : "28px 46px 22px 47px",
                    gap: "24px",
                    width: "100%",
                    textAlign: "left",
                    fontSize: "22px",
                    color: "#242929",
                    fontFamily: '"Poppins", sans-serif',
                    overflowX: "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      padding: "0px 0px 6px",
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <Typography
                      id="drivers"
                      variant="h3"
                      sx={{
                        margin: "0",
                        position: "relative",
                        fontSize: "inherit",
                        lineHeight: "20px",
                        fontWeight: "600",
                        display: "inline-block",
                        minWidth: "115px",
                        zIndex: "1",
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      Driver
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      padding: "0px 0px 8px",
                      gap: "15px",
                      fontSize: "16px",
                      fontFamily: '"Poppins", sans-serif',
                      color: "#888",
                    }}
                  >
                    <div style={{ width: "100%", overflowX: "auto" }}>
                      <table className={classes.table}>
                        <thead className={classes.thead}>
                          <tr>
                            <th className={classes.th}>#</th>
                            <th className={classes.th}>First Name</th>
                            <th className={classes.th}>Last Name</th>
                            <th className={classes.th}>Marital Status</th>
                            <th className={classes.th}>Date of Birth</th>
                            <th className={classes.th}>Gender</th>
                            <th className={classes.th}>Violations</th>
                            <th className={classes.th}>Actions</th>
                          </tr>
                        </thead>
                        <tbody className={classes.tbody}>
                          {isLoading ? (
                            <tr>
                              <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                <Loader />
                              </td>
                            </tr>
                          ) : rows.length === 0 ? (
                            <tr>
                              <td
                                className={classes.td}
                                colSpan="8"
                                style={{
                                  textAlign: "center",
                                  padding: "20px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: "#888",
                                }}
                              >
                                No driver found
                              </td>
                            </tr>
                          ) : (
                            paginatedRows.map((row) => (
                              <tr key={row.id}>
                                <td className={classes.td}>{row.driverNumber}</td>
                                <td className={classes.td}>{row.firstName}</td>
                                <td className={classes.td}>{row.lastName}</td>
                                <td className={classes.td}>
                                  {row.marital === "M"
                                    ? "Married"
                                    : row.marital === "S"
                                    ? "Single"
                                    : row.marital}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }} className={classes.td}>
                                  {row.dob}
                                </td>
                                <td className={classes.td}>
                                  {row.gender === "M"
                                    ? "Male"
                                    : row.gender === "F"
                                    ? "Female"
                                    : row.gender}
                                </td>
                                <td className={classes.td}>
                                  <Box sx={{ display: "flex", gap: "11.5px", alignItems: "center" }}>
                                    <IconButton
                                      id="editicon"
                                      onClick={() => handleOpenDialogDriverViolation(row.id)}
                                      size={isMobile ? "small" : "medium"}
                                    >
                                      <EditIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                  </Box>
                                </td>
                                <td className={classes.td}>
                                  <Box sx={{ display: "flex", gap: "1.5px", alignItems: "center" }}>
                                    <IconButton
                                      id="editicon"
                                      onClick={handleEditClick(row.id)}
                                      size={isMobile ? "small" : "medium"}
                                    >
                                      <EditIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                    <IconButton
                                      id="deleteicon"
                                      onClick={() => handleOpenDialog(row.id)}
                                      size={isMobile ? "small" : "medium"}
                                    >
                                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                  </Box>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <DriverViolationsDialog
                      open={dialogOpen}
                      handleClose={handleCloseDialogViolation}
                      driverId={selectedDriverId}
                      onSave={handleSaveViolations}
                      existingViolations={
                        selectedDriverId
                          ? getDriverViolations(selectedDriverId)
                          : []
                      }
                    />
                  </Box>

                  {rows.length > 0 && (
                    <Box sx={{ width: "100%", overflow: "hidden", padding: "16px 0" }}>
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        justifyContent="flex-end"
                        alignItems={isMobile ? "flex-start" : "center"}
                        flexWrap="wrap"
                        spacing={2}
                      >
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" sx={{ fontSize: "12px", marginRight: "8px" }}>
                            Rows per page:
                          </Typography>
                          <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                              setRowsPerPage(parseInt(e.target.value, 10));
                              setPage(0);
                            }}
                            size={isMobile ? "small" : "medium"}
                            sx={{ fontSize: "12px", minWidth: "80px" }}
                          >
                            {[5, 10, 15, 25, 50, 100].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>

                        <Pagination
                          count={Math.ceil(rows.length / rowsPerPage)}
                          page={page + 1}
                          onChange={(e, value) => setPage(value - 1)}
                          showFirstButton
                          showLastButton
                          siblingCount={1}
                          boundaryCount={1}
                          size={isMobile ? "small" : "medium"}
                        />
                      </Stack>
                    </Box>
                  )}

                  <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                  >
                    <DialogTitle
                      id="alert-dialog-title"
                    >
                      {"Confirm Delete"}
                    </DialogTitle>
                    <DialogContent sx={{ marginTop: "15px" }}>
                      Are you sure you want to delete this driver? This action cannot be undone.
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        size='large'
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleConfirmDelete}
                        variant="outlined"
                        size='large'
                      >
                        Delete
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Paper>

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "222px",
                    height: "42px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    fontFamily: '"Poppins", sans-serif',
                    gap: "14px",
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIosIcon />}
                    disableElevation={true}
                    variant="outlined"
                    id="backbutton"
                    onClick={handleBack}
                  >
                    Back
                  </Button>

                  <Button
                    endIcon={<ArrowForwardIosIcon />}
                    disableElevation={true}
                    variant="outlined"
                    id="nextbutton"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    fontFamily: '"Poppins", sans-serif',
                  }}
                >
                  <Button
                    disableElevation={true}
                    variant="contained"
                    id="continuebutton"
                    onClick={handleNext}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
});

export default DriverInformationList;
